import { z } from "zod";

import {
  attemptKinds,
  caseDimensionKeys,
  caseSubmissionStatuses,
  caseTypes,
  dayTypes,
  knowledgeMapNodeKinds,
  knowledgeMapScopes,
  learningMethods,
  macroBlockKinds,
  macroBlockStatuses,
  masteryLevels,
  mistakeCauseCodes,
  mistakeDetailStatuses,
  mistakeStatuses,
  planChangeKinds,
  planningAuditStages,
  planRevisionStatuses,
  practiceModes,
  practiceEvidenceDetailStatuses,
  practiceTimingEvidenceStatuses,
  priorityLevels,
  reportStatuses,
  reportSubmissionTimings,
  reviewActors,
  reviewEventKinds,
  reviewIntervals,
  reviewResultStatuses,
  sessionKinds,
  sessionStatuses,
  studyDayStatuses,
  subjects,
} from "./models";
import { classifyDailyReportTiming, isFutureStudyDate } from "./report-timing";

const isoDatePattern = /^\d{4}-\d{2}-\d{2}$/;

function isRealIsoDate(value: string): boolean {
  if (!isoDatePattern.test(value)) return false;
  const [yearText, monthText, dayText] = value.split("-");
  if (!yearText || !monthText || !dayText) return false;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
}

export const isoDateSchema = z
  .string()
  .regex(isoDatePattern, "日期必须为 YYYY-MM-DD")
  .refine(isRealIsoDate, "日期无效");

export const timestampSchema = z.string().datetime({ offset: true });
export const nonEmptyStringSchema = z.string().trim().min(1);
export const identifierSchema = z.string().trim().min(1).max(160);

export const knowledgeMapNodeSchema = z.object({
  id: identifierSchema,
  kind: z.enum(knowledgeMapNodeKinds),
  label: nonEmptyStringSchema,
  detail: nonEmptyStringSchema.optional(),
  priority: z.enum(priorityLevels).optional(),
  mastery: z.enum(masteryLevels).optional(),
  sourceRefs: z.array(nonEmptyStringSchema).optional(),
});

export const knowledgeMapEdgeSchema = z.object({
  id: identifierSchema,
  from: identifierSchema,
  to: identifierSchema,
  label: nonEmptyStringSchema.optional(),
});

export const knowledgeMapSchema = z
  .object({
    id: identifierSchema,
    scope: z.enum(knowledgeMapScopes),
    title: nonEmptyStringSchema,
    version: z.number().int().positive(),
    nodes: z.array(knowledgeMapNodeSchema).min(2),
    edges: z.array(knowledgeMapEdgeSchema),
    sourceRefs: z.array(nonEmptyStringSchema),
    updatedReason: nonEmptyStringSchema.optional(),
  })
  .superRefine((map, context) => {
    const nodeIds = new Set(map.nodes.map((node) => node.id));
    if (nodeIds.size !== map.nodes.length) {
      context.addIssue({ code: "custom", message: "知识导图节点 ID 不得重复", path: ["nodes"] });
    }
    const edgeIds = new Set(map.edges.map((edge) => edge.id));
    if (edgeIds.size !== map.edges.length) {
      context.addIssue({ code: "custom", message: "知识导图边 ID 不得重复", path: ["edges"] });
    }
    for (const [index, edge] of map.edges.entries()) {
      if (!nodeIds.has(edge.from) || !nodeIds.has(edge.to)) {
        context.addIssue({
          code: "custom",
          message: "知识导图边必须引用现有节点",
          path: ["edges", index],
        });
      }
    }
  });

export const chapterKnowledgeMapSchema = knowledgeMapSchema.refine((map) => map.scope === "chapter", {
  message: "章节总图必须使用 chapter 范围",
  path: ["scope"],
});

const requiredSessionMapKinds = ["concept", "pitfall", "question_type", "output"] as const;

export const sessionKnowledgeMapSchema = knowledgeMapSchema.superRefine((map, context) => {
  if (map.scope !== "session") {
    context.addIssue({ code: "custom", message: "学习 Session 必须使用 session 范围导图", path: ["scope"] });
  }

  const kinds = new Set(map.nodes.map((node) => node.kind));
  for (const kind of requiredSessionMapKinds) {
    if (!kinds.has(kind)) {
      context.addIssue({ code: "custom", message: `Session 导图缺少 ${kind} 节点`, path: ["nodes"] });
    }
  }
  if (!["rule", "formula", "threshold"].some((kind) => kinds.has(kind as never))) {
    context.addIssue({
      code: "custom",
      message: "Session 导图必须包含规则、公式或阈值节点",
      path: ["nodes"],
    });
  }
});

export const studySourceSchema = z.object({
  id: identifierSchema,
  title: nonEmptyStringSchema,
  documentId: nonEmptyStringSchema.optional(),
  bookPages: nonEmptyStringSchema.optional(),
  pdfPages: nonEmptyStringSchema.optional(),
  knowledgeModuleId: nonEmptyStringSchema.optional(),
  note: nonEmptyStringSchema.optional(),
});

export const acceptanceCriterionSchema = z.object({
  id: identifierSchema,
  description: nonEmptyStringSchema,
  metric: z.enum(["count", "rate", "minutes", "binary", "coverage"]).optional(),
  target: z.number().nonnegative().optional(),
  unit: nonEmptyStringSchema.optional(),
});

export const studyStepSchema = z.object({
  id: identifierSchema,
  order: z.number().int().positive(),
  title: nonEmptyStringSchema,
  description: nonEmptyStringSchema,
  plannedMinutes: z.number().int().positive().max(55),
});

export const studyLessonReferenceSchema = z.object({
  title: nonEmptyStringSchema,
  locator: nonEmptyStringSchema.optional(),
  url: z.url().optional(),
});

export const studyLessonExampleSchema = z.object({
  id: identifierSchema,
  prompt: nonEmptyStringSchema,
  answer: nonEmptyStringSchema,
  reasoning: z.array(nonEmptyStringSchema).min(1),
  trap: nonEmptyStringSchema.optional(),
});

export const studyLessonMemoryAidItemSchema = z.object({
  token: nonEmptyStringSchema,
  meaning: nonEmptyStringSchema,
});

export const studyLessonSectionSchema = z.object({
  id: identifierSchema,
  title: nonEmptyStringSchema,
  priority: z.enum(priorityLevels),
  knowledgeMapNodeIds: z.array(identifierSchema).optional(),
  hardestPoint: nonEmptyStringSchema.optional(),
  explanation: z.array(nonEmptyStringSchema).min(2),
  memoryAid: nonEmptyStringSchema.optional(),
  memoryAidBreakdown: z.array(studyLessonMemoryAidItemSchema).min(1).optional(),
  examTriggers: z.array(nonEmptyStringSchema).min(1).optional(),
  pitfalls: z.array(nonEmptyStringSchema).min(1),
  examples: z.array(studyLessonExampleSchema).min(1),
});

export const studyLessonComparisonSchema = z
  .object({
    title: nonEmptyStringSchema,
    columns: z.array(nonEmptyStringSchema).min(2),
    rows: z.array(z.array(nonEmptyStringSchema).min(2)).min(1),
  })
  .superRefine((comparison, context) => {
    comparison.rows.forEach((row, index) => {
      if (row.length !== comparison.columns.length) {
        context.addIssue({
          code: "custom",
          message: "对比表每行单元格数必须与表头一致",
          path: ["rows", index],
        });
      }
    });
  });

export const studyLessonSchema = z.object({
  overview: nonEmptyStringSchema,
  learningStrategy: z.array(nonEmptyStringSchema).min(1),
  textbookLocator: nonEmptyStringSchema,
  references: z.array(studyLessonReferenceSchema).min(1).optional(),
  sections: z.array(studyLessonSectionSchema).min(2),
  comparisons: z.array(studyLessonComparisonSchema).min(1).optional(),
  recallPrompts: z.array(nonEmptyStringSchema).min(1).optional(),
});

export const practiceSessionActivitySchema = z
  .object({
    type: z.literal("practice"),
    externalSource: nonEmptyStringSchema,
    selectionPlan: z.array(z.object({
      cluster: nonEmptyStringSchema,
      count: z.number().int().positive(),
    })).min(1),
    targetQuestions: z.number().int().positive().max(30),
    targetRate: z.number().min(0).max(1),
  })
  .superRefine((activity, context) => {
    const selectedQuestions = activity.selectionPlan.reduce((sum, item) => sum + item.count, 0);
    if (selectedQuestions !== activity.targetQuestions) {
      context.addIssue({
        code: "custom",
        message: "选题配额合计必须等于本段目标题量",
        path: ["selectionPlan"],
      });
    }
  });

export const caseSessionActivitySchema = z
  .object({
    type: z.literal("case"),
    stem: nonEmptyStringSchema,
    facts: z.array(nonEmptyStringSchema).min(1),
    questions: z.array(z.object({
      id: identifierSchema,
      prompt: nonEmptyStringSchema,
      points: z.number().positive(),
      responseType: z.enum(["single", "multiple", "boolean", "written"]).optional(),
      correctAnswer: nonEmptyStringSchema.optional(),
      attemptKind: z.enum(attemptKinds).optional(),
    })).min(1),
    rubric: z.array(z.object({
      questionId: identifierSchema,
      scoringPoints: z.array(nonEmptyStringSchema).min(1),
    })).min(1),
    referenceOutline: z.array(nonEmptyStringSchema).min(1),
  })
  .superRefine((activity, context) => {
    const questionIds = new Set(activity.questions.map((question) => question.id));
    if (questionIds.size !== activity.questions.length) {
      context.addIssue({ code: "custom", message: "案例问题 ID 不得重复", path: ["questions"] });
    }
    const rubricIds = new Set(activity.rubric.map((item) => item.questionId));
    if (rubricIds.size !== activity.rubric.length) {
      context.addIssue({ code: "custom", message: "案例评分项不得重复", path: ["rubric"] });
    }
    activity.rubric.forEach((item, index) => {
      if (!questionIds.has(item.questionId)) {
        context.addIssue({
          code: "custom",
          message: "案例评分项必须引用现有问题",
          path: ["rubric", index, "questionId"],
        });
      }
    });
    activity.questions.forEach((question, index) => {
      if (!rubricIds.has(question.id)) {
        context.addIssue({
          code: "custom",
          message: "每道案例问题必须有评分点",
          path: ["questions", index, "id"],
        });
      }
    });
  });

export const simulationActivitySectionSchema = z
  .object({
    subject: z.enum(subjects),
    questionCount: z.number().int().positive(),
    targetCorrect: z.number().int().nonnegative(),
  })
  .refine((section) => section.targetCorrect <= section.questionCount, {
    message: "模拟分科目标答对数不得超过该科题数",
    path: ["targetCorrect"],
  });

export const simulationSessionActivitySchema = z
  .object({
    type: z.literal("simulation"),
    timeLimitMinutes: z.number().int().positive(),
    sections: z.array(simulationActivitySectionSchema).min(1),
    totalTargetCorrect: z.number().int().nonnegative(),
  })
  .superRefine((activity, context) => {
    const subjectSet = new Set(activity.sections.map((section) => section.subject));
    if (subjectSet.size !== activity.sections.length) {
      context.addIssue({
        code: "custom",
        message: "模拟测试分科不得重复",
        path: ["sections"],
      });
    }
    const questionTotal = activity.sections.reduce((total, section) => total + section.questionCount, 0);
    const sectionTargetTotal = activity.sections.reduce(
      (total, section) => total + section.targetCorrect,
      0,
    );
    if (activity.totalTargetCorrect > questionTotal) {
      context.addIssue({
        code: "custom",
        message: "模拟总目标答对数不得超过总题数",
        path: ["totalTargetCorrect"],
      });
    }
    if (activity.totalTargetCorrect !== sectionTargetTotal) {
      context.addIssue({
        code: "custom",
        message: "模拟总目标答对数必须等于各科目标答对数之和",
        path: ["totalTargetCorrect"],
      });
    }
  });

export const sessionActivitySchema = z.discriminatedUnion("type", [
  practiceSessionActivitySchema,
  caseSessionActivitySchema,
  simulationSessionActivitySchema,
]);

export const studySessionSchema = z
  .object({
    id: identifierSchema,
    order: z.number().int().positive(),
    title: nonEmptyStringSchema,
    kind: z.enum(sessionKinds),
    plannedMinutes: z.number().int().min(45).max(55),
    priority: z.enum(priorityLevels),
    methods: z.array(z.enum(learningMethods)).min(1),
    objectives: z.array(nonEmptyStringSchema).min(1),
    knowledgeMap: sessionKnowledgeMapSchema,
    lesson: studyLessonSchema.optional(),
    activity: sessionActivitySchema.optional(),
    sources: z.array(studySourceSchema),
    steps: z.array(studyStepSchema).min(1),
    acceptanceCriteria: z.array(acceptanceCriterionSchema).min(1),
    requiredEvidence: z.array(nonEmptyStringSchema).min(1),
    status: z.enum(sessionStatuses),
    actualMinutes: z.number().int().nonnegative().optional(),
    startedAt: timestampSchema.optional(),
    endedAt: timestampSchema.optional(),
    incompleteReason: nonEmptyStringSchema.optional(),
  })
  .superRefine((session, context) => {
    const stepMinutes = session.steps.reduce((total, step) => total + step.plannedMinutes, 0);
    if (stepMinutes !== session.plannedMinutes) {
      context.addIssue({
        code: "custom",
        message: "学习步骤分钟数之和必须等于 Session 计划分钟数",
        path: ["steps"],
      });
    }
    if (["partial", "skipped"].includes(session.status) && !session.incompleteReason) {
      context.addIssue({
        code: "custom",
        message: "部分完成或跳过时必须填写原因",
        path: ["incompleteReason"],
      });
    }
  });

export const macroBlockSchema = z
  .object({
    id: identifierSchema,
    order: z.number().int().positive(),
    subject: z.enum(subjects),
    title: nonEmptyStringSchema,
    kind: z.enum(macroBlockKinds),
    recommendedWindow: nonEmptyStringSchema,
    plannedMinutes: z.number().int().min(90).max(180),
    chapterMapId: identifierSchema,
    sessions: z.array(studySessionSchema).min(1),
    status: z.enum(macroBlockStatuses),
    actualMinutes: z.number().int().nonnegative().optional(),
    incompleteReason: nonEmptyStringSchema.optional(),
  })
  .superRefine((block, context) => {
    const sessionIds = new Set(block.sessions.map((session) => session.id));
    if (sessionIds.size !== block.sessions.length) {
      context.addIssue({ code: "custom", message: "宏块内 Session ID 不得重复", path: ["sessions"] });
    }
    if (["partial", "skipped"].includes(block.status) && !block.incompleteReason) {
      context.addIssue({
        code: "custom",
        message: "部分完成或跳过宏块时必须填写原因",
        path: ["incompleteReason"],
      });
    }
  });

export const studyDaySchema = z
  .object({
    id: identifierSchema,
    date: isoDateSchema,
    timezone: nonEmptyStringSchema,
    phase: nonEmptyStringSchema,
    dayType: z.enum(dayTypes),
    title: nonEmptyStringSchema,
    summary: nonEmptyStringSchema,
    status: z.enum(studyDayStatuses),
    planVersion: z.number().int().positive(),
    contentFormatVersion: z.number().int().positive().optional(),
    planningAuditTrail: z.array(z.object({
      stage: z.enum(planningAuditStages),
      checkedAt: timestampSchema,
      evidence: z.array(nonEmptyStringSchema).min(1),
      result: nonEmptyStringSchema,
      changed: z.boolean(),
      changeSummary: nonEmptyStringSchema,
    })).length(3).optional(),
    plannedMinutes: z.number().int().positive(),
    availableMinutes: z.number().int().nonnegative(),
    chapterMaps: z.array(chapterKnowledgeMapSchema).min(1),
    macroBlocks: z.array(macroBlockSchema).min(1),
    issuedAt: timestampSchema.optional(),
    acknowledgedAt: timestampSchema.optional(),
    startedAt: timestampSchema.optional(),
    submittedAt: timestampSchema.optional(),
    acceptedAt: timestampSchema.optional(),
  })
  .superRefine((day, context) => {
    if (day.planningAuditTrail) {
      const expectedStages = ["initial_generation", "gap_check_1", "gap_check_2"] as const;
      day.planningAuditTrail.forEach((entry, index) => {
        if (entry.stage !== expectedStages[index]) {
          context.addIssue({
            code: "custom",
            message: "计划审计须按初步生成、查漏1、查漏2顺序记录",
            path: ["planningAuditTrail", index, "stage"],
          });
        }
      });
    }
    const chapterMapIds = new Set(day.chapterMaps.map((map) => map.id));
    const blockIds = new Set(day.macroBlocks.map((block) => block.id));
    const referencedBlockMapIds = new Set<string>();
    const sessionIds = new Set<string>();
    const sessionMapIds = new Set<string>();
    const sessionNodeIds = new Set<string>();
    const requiresDetailedLesson = day.planVersion >= 3 || (day.contentFormatVersion ?? 1) >= 3;
    if (chapterMapIds.size !== day.chapterMaps.length) {
      context.addIssue({ code: "custom", message: "每日块级知识导图 ID 不得重复", path: ["chapterMaps"] });
    }
    if (blockIds.size !== day.macroBlocks.length) {
      context.addIssue({ code: "custom", message: "每日宏块 ID 不得重复", path: ["macroBlocks"] });
    }

    day.macroBlocks.forEach((block, blockIndex) => {
      if (!chapterMapIds.has(block.chapterMapId)) {
        context.addIssue({
          code: "custom",
          message: "宏块必须引用当日章节总图",
          path: ["macroBlocks", blockIndex, "chapterMapId"],
        });
      }
      if (referencedBlockMapIds.has(block.chapterMapId)) {
        context.addIssue({
          code: "custom",
          message: "每个宏块必须引用独立知识导图，不得与其他宏块复用",
          path: ["macroBlocks", blockIndex, "chapterMapId"],
        });
      }
      referencedBlockMapIds.add(block.chapterMapId);
      block.sessions.forEach((session, sessionIndex) => {
        session.knowledgeMap.nodes.forEach((node, nodeIndex) => {
          if (sessionNodeIds.has(node.id)) {
            context.addIssue({
              code: "custom",
              message: "业务计划中各学习段的导图节点 ID 必须全局唯一，避免跨段引用歧义",
              path: ["macroBlocks", blockIndex, "sessions", sessionIndex, "knowledgeMap", "nodes", nodeIndex, "id"],
            });
          }
          sessionNodeIds.add(node.id);
        });
        if (requiresDetailedLesson && !session.lesson) {
          context.addIssue({
            code: "custom",
            message: "业务计划版本 3 起每个学习段必须包含导图配套精讲，不得通过省略内容格式版本绕过",
            path: ["macroBlocks", blockIndex, "sessions", sessionIndex, "lesson"],
          });
        }
        if (requiresDetailedLesson && session.lesson) {
          const mapNodeIds = new Set(session.knowledgeMap.nodes.map((node) => node.id));
          const incomingNodeIds = new Set(session.knowledgeMap.edges.map((edge) => edge.to));
          const centerNodeId = session.knowledgeMap.nodes.find((node) => !incomingNodeIds.has(node.id))?.id
            ?? session.knowledgeMap.nodes[0]?.id;
          const highValueNodeIds = session.knowledgeMap.nodes
            .filter((node) => node.id !== centerNodeId && (node.priority === "S" || node.priority === "A"))
            .map((node) => node.id);
          const coveredNodeIds = new Set<string>();

          session.lesson.sections.forEach((section, sectionIndex) => {
            const sectionPath = ["macroBlocks", blockIndex, "sessions", sessionIndex, "lesson", "sections", sectionIndex] as const;
            if (!section.knowledgeMapNodeIds?.length) {
              context.addIssue({
                code: "custom",
                message: "业务计划版本 3 起每个精讲小节必须引用至少一个本段导图节点",
                path: [...sectionPath, "knowledgeMapNodeIds"],
              });
            }
            if (!section.hardestPoint?.trim()) {
              context.addIssue({
                code: "custom",
                message: "业务计划版本 3 起每个精讲小节必须明确本节最难点",
                path: [...sectionPath, "hardestPoint"],
              });
            }
            section.knowledgeMapNodeIds?.forEach((nodeId, nodeIndex) => {
              if (!mapNodeIds.has(nodeId)) {
                context.addIssue({
                  code: "custom",
                  message: "精讲小节引用的导图节点不存在于本学习段",
                  path: [...sectionPath, "knowledgeMapNodeIds", nodeIndex],
                });
                return;
              }
              coveredNodeIds.add(nodeId);
            });
            if (section.memoryAid && !section.memoryAidBreakdown?.length) {
              context.addIssue({
                code: "custom",
                message: "业务计划版本 3 起有记忆口诀的精讲小节必须提供非空逐词释义",
                path: [...sectionPath, "memoryAidBreakdown"],
              });
            }
          });

          const uncoveredHighValueNodeIds = highValueNodeIds.filter((nodeId) => !coveredNodeIds.has(nodeId));
          if (uncoveredHighValueNodeIds.length) {
            context.addIssue({
              code: "custom",
              message: `本段精讲必须覆盖所有非中心 S/A 级导图节点；尚缺：${uncoveredHighValueNodeIds.join("、")}`,
              path: ["macroBlocks", blockIndex, "sessions", sessionIndex, "lesson", "sections"],
            });
          }
        }
        if (!requiresDetailedLesson && (day.contentFormatVersion ?? 1) >= 2 && !session.lesson) {
          context.addIssue({
            code: "custom",
            message: "教学内容格式版本 2 起每个学习段必须包含浓缩教材",
            path: ["macroBlocks", blockIndex, "sessions", sessionIndex, "lesson"],
          });
        }
        if ((day.contentFormatVersion ?? 1) >= 2 && session.kind === "practice" && session.activity?.type !== "practice") {
          context.addIssue({
            code: "custom",
            message: "教学内容格式版本 2 起章节练习段必须声明题库活动与题量",
            path: ["macroBlocks", blockIndex, "sessions", sessionIndex, "activity"],
          });
        }
        if ((day.contentFormatVersion ?? 1) >= 2 && session.kind === "case_training" && session.activity?.type !== "case") {
          context.addIssue({
            code: "custom",
            message: "教学内容格式版本 2 起案例段必须包含题干、问题和评分点",
            path: ["macroBlocks", blockIndex, "sessions", sessionIndex, "activity"],
          });
        }
        if ((day.contentFormatVersion ?? 1) >= 2 && session.kind === "simulation" && session.activity?.type !== "simulation") {
          context.addIssue({
            code: "custom",
            message: "教学内容格式版本 2 起模拟段必须声明分科题量、限时和目标",
            path: ["macroBlocks", blockIndex, "sessions", sessionIndex, "activity"],
          });
        }
        if (sessionIds.has(session.id)) {
          context.addIssue({
            code: "custom",
            message: "每日 Session ID 不得重复",
            path: ["macroBlocks", blockIndex, "sessions", sessionIndex, "id"],
          });
        }
        sessionIds.add(session.id);
        if (sessionMapIds.has(session.knowledgeMap.id)) {
          context.addIssue({
            code: "custom",
            message: "每个45—55分钟学习段必须使用独立知识导图",
            path: ["macroBlocks", blockIndex, "sessions", sessionIndex, "knowledgeMap", "id"],
          });
        }
        sessionMapIds.add(session.knowledgeMap.id);
      });
    });
  });

export const confidenceCountsSchema = z.object({
  sure: z.number().int().nonnegative(),
  uncertain: z.number().int().nonnegative(),
  unknown: z.number().int().nonnegative(),
});

export const practiceAttemptSchema = z
  .object({
    id: identifierSchema,
    dayId: identifierSchema,
    sessionId: identifierSchema,
    subject: z.enum(subjects),
    chapter: nonEmptyStringSchema,
    topic: nonEmptyStringSchema.optional(),
    source: nonEmptyStringSchema,
    mode: z.enum(practiceModes),
    attemptKind: z.enum(attemptKinds),
    rawQuestionCount: z.number().int().positive(),
    duplicateQuestionCount: z.number().int().nonnegative(),
    correctCount: z.number().int().nonnegative(),
    wrongCount: z.number().int().nonnegative(),
    unansweredCount: z.number().int().nonnegative(),
    understoodQuestionCount: z.number().int().nonnegative(),
    elapsedMinutes: z.number().positive().max(240).nullable(),
    lookedUpDuringAttempt: z.boolean(),
    confidence: confidenceCountsSchema,
    evidenceDetailStatus: z.enum(practiceEvidenceDetailStatuses).optional().default("detailed"),
    timingEvidenceStatus: z.enum(practiceTimingEvidenceStatuses).optional(),
    startedAt: timestampSchema.optional(),
    endedAt: timestampSchema.optional(),
    notes: nonEmptyStringSchema.optional(),
  })
  .superRefine((attempt, context) => {
    const effectiveCount = attempt.rawQuestionCount - attempt.duplicateQuestionCount;
    if (effectiveCount <= 0) {
      context.addIssue({
        code: "custom",
        message: "扣除重复题后必须至少有 1 道有效题",
        path: ["duplicateQuestionCount"],
      });
      return;
    }
    if (attempt.correctCount + attempt.wrongCount + attempt.unansweredCount !== effectiveCount) {
      context.addIssue({
        code: "custom",
        message: "正确、错误、未答之和必须等于有效题量",
        path: ["correctCount"],
      });
    }
    if (attempt.understoodQuestionCount > effectiveCount) {
      context.addIssue({
        code: "custom",
        message: "读懂题干数不得大于有效题量",
        path: ["understoodQuestionCount"],
      });
    }
    const timingEvidenceStatus = attempt.timingEvidenceStatus
      ?? (attempt.elapsedMinutes === null ? "unknown" : "measured");
    if (timingEvidenceStatus === "unknown" && attempt.elapsedMinutes !== null) {
      context.addIssue({
        code: "custom",
        message: "用时未知时不得填写分钟数",
        path: ["elapsedMinutes"],
      });
    }
    if (timingEvidenceStatus === "measured" && attempt.elapsedMinutes === null) {
      context.addIssue({
        code: "custom",
        message: "已计时时必须填写分钟数",
        path: ["elapsedMinutes"],
      });
    }
    const confidenceTotal =
      attempt.confidence.sure + attempt.confidence.uncertain + attempt.confidence.unknown;
    if (confidenceTotal !== effectiveCount) {
      context.addIssue({
        code: "custom",
        message: "信心标记合计必须等于有效题量",
        path: ["confidence"],
      });
    }
  });

export const mistakeReviewItemSchema = z
  .object({
    interval: z.enum(reviewIntervals),
    dueDate: isoDateSchema,
    status: z.enum(reviewResultStatuses),
    completedAt: timestampSchema.optional(),
    note: nonEmptyStringSchema.optional(),
  })
  .superRefine((review, context) => {
    if (review.status !== "pending" && !review.completedAt) {
      context.addIssue({ code: "custom", message: "已处理回炉项必须记录完成时间", path: ["completedAt"] });
    }
  });

export const mistakeSchema = z.object({
  id: identifierSchema,
  createdDate: isoDateSchema,
  dayId: identifierSchema,
  practiceAttemptId: identifierSchema.optional(),
  subject: z.enum(subjects),
  knowledgeCluster: nonEmptyStringSchema,
  questionType: nonEmptyStringSchema,
  questionSummary: nonEmptyStringSchema,
  learnerAnswer: nonEmptyStringSchema,
  correctConclusion: nonEmptyStringSchema,
  evidence: nonEmptyStringSchema,
  causeCode: z.enum(mistakeCauseCodes),
  preventionRule: nonEmptyStringSchema,
  attachmentIds: z.array(identifierSchema),
  knowledgeMapNodeIds: z.array(identifierSchema),
  reviews: z.array(mistakeReviewItemSchema).length(3),
  status: z.enum(mistakeStatuses),
  detailStatus: z.enum(mistakeDetailStatuses).optional(),
});

export const caseDimensionAssessmentSchema = z
  .object({
    dimension: z.enum(caseDimensionKeys),
    score: z.number().nonnegative().optional(),
    maxScore: z.number().positive().optional(),
    note: nonEmptyStringSchema.optional(),
  })
  .superRefine((assessment, context) => {
    if (
      assessment.score !== undefined &&
      assessment.maxScore !== undefined &&
      assessment.score > assessment.maxScore
    ) {
      context.addIssue({ code: "custom", message: "维度得分不得超过满分", path: ["score"] });
    }
  });

export const caseQuestionResponseSchema = z
  .object({
    questionId: identifierSchema,
    response: nonEmptyStringSchema,
    score: z.number().nonnegative(),
    maxScore: z.number().positive(),
    autoGraded: z.boolean(),
    attemptKind: z.enum(attemptKinds).optional(),
  })
  .refine((response) => response.score <= response.maxScore, {
    message: "案例题得分不得超过满分",
    path: ["score"],
  });

export const caseSubmissionSchema = z
  .object({
    id: identifierSchema,
    dayId: identifierSchema,
    sessionId: identifierSchema,
    title: nonEmptyStringSchema,
    caseType: z.enum(caseTypes),
    source: nonEmptyStringSchema,
    closedBook: z.boolean(),
    elapsedMinutes: z.number().positive().max(240),
    answerAttachmentIds: z.array(identifierSchema),
    referenceAttachmentIds: z.array(identifierSchema),
    answerText: nonEmptyStringSchema.optional(),
    questionResponses: z.array(caseQuestionResponseSchema).optional(),
    selfCoverageRate: z.number().min(0).max(1).optional(),
    dimensions: z.array(caseDimensionAssessmentSchema),
    missingPoints: z.array(nonEmptyStringSchema),
    rewriteScope: nonEmptyStringSchema.optional(),
    rewriteDueDate: isoDateSchema.optional(),
    parentSubmissionId: identifierSchema.optional(),
    version: z.number().int().positive(),
    status: z.enum(caseSubmissionStatuses),
    submittedAt: timestampSchema.optional(),
  })
  .superRefine((submission, context) => {
    const hasQuestionResponse = submission.questionResponses?.some((response) => response.response.trim()) ?? false;
    if (submission.status !== "draft" && submission.answerAttachmentIds.length === 0 && !submission.answerText && !hasQuestionResponse) {
      context.addIssue({
        code: "custom",
        message: "提交案例时必须提供逐题答案、答案文字或手写照片",
        path: ["answerAttachmentIds"],
      });
    }
    const questionIds = new Set(submission.questionResponses?.map((response) => response.questionId) ?? []);
    if (questionIds.size !== (submission.questionResponses?.length ?? 0)) {
      context.addIssue({ code: "custom", message: "案例逐题答案不得重复", path: ["questionResponses"] });
    }
    if (submission.status === "revision_required" && !submission.rewriteScope) {
      context.addIssue({ code: "custom", message: "要求重写时必须写明范围", path: ["rewriteScope"] });
    }
  });

export const blockOutcomeSchema = z
  .object({
    macroBlockId: identifierSchema,
    status: z.enum(["completed", "partial", "skipped"]),
    actualMinutes: z.number().int().nonnegative(),
    incompleteReason: nonEmptyStringSchema.optional(),
  })
  .superRefine((outcome, context) => {
    if (outcome.status !== "completed" && !outcome.incompleteReason) {
      context.addIssue({ code: "custom", message: "未完整完成时必须说明原因", path: ["incompleteReason"] });
    }
  });

export const dailyReportSchema = z
  .object({
    id: identifierSchema,
    dayId: identifierSchema,
    date: isoDateSchema,
    version: z.number().int().positive(),
    status: z.enum(reportStatuses),
    submissionTiming: z.enum(reportSubmissionTimings).optional(),
    delayReason: nonEmptyStringSchema.optional(),
    sleepHours: z.number().min(0).max(24).optional(),
    effectiveStudyMinutes: z.number().int().nonnegative().max(1440).optional(),
    energyLevel: z.number().int().min(1).max(5).optional(),
    concentrationLevel: z.number().int().min(1).max(5).optional(),
    healthConstraints: nonEmptyStringSchema.optional(),
    hardConstraints: nonEmptyStringSchema.optional(),
    blockOutcomes: z.array(blockOutcomeSchema),
    practiceAttemptIds: z.array(identifierSchema),
    mistakeIds: z.array(identifierSchema),
    caseSubmissionIds: z.array(identifierSchema),
    unknownPoints: z.array(nonEmptyStringSchema).max(3),
    mostEffectiveMethod: nonEmptyStringSchema.optional(),
    mostWastefulMethod: nonEmptyStringSchema.optional(),
    tomorrowAvailableMinutes: z.number().int().nonnegative().max(1440).optional(),
    attachmentIds: z.array(identifierSchema),
    submittedAt: timestampSchema.optional(),
    acceptedAt: timestampSchema.optional(),
  })
  .superRefine((report, context) => {
    if (report.status === "draft") return;
    const required: Array<[unknown, string, (string | number)[]]> = [
      [report.sleepHours, "提交晚报时必须填写睡眠", ["sleepHours"]],
      [report.effectiveStudyMinutes, "提交晚报时必须填写有效学习分钟", ["effectiveStudyMinutes"]],
      [report.energyLevel, "提交晚报时必须填写精力", ["energyLevel"]],
      [report.concentrationLevel, "提交晚报时必须填写专注度", ["concentrationLevel"]],
      [report.tomorrowAvailableMinutes, "提交晚报时必须填写次日可用时间", ["tomorrowAvailableMinutes"]],
      [report.submittedAt, "提交晚报时必须记录提交时间", ["submittedAt"]],
    ];
    for (const [value, message, path] of required) {
      if (value === undefined) context.addIssue({ code: "custom", message, path });
    }
    if (report.submittedAt) {
      const submittedAt = new Date(report.submittedAt);
      if (isFutureStudyDate(report.date, submittedAt)) {
        context.addIssue({
          code: "custom",
          message: "不能在学习日期到来前提交晚报",
          path: ["submittedAt"],
        });
      }
      const actualTiming = classifyDailyReportTiming(report.date, submittedAt);
      if (report.submissionTiming && report.submissionTiming !== actualTiming) {
        context.addIssue({
          code: "custom",
          message: "晚报时效分类必须与学习日期及上海提交时间一致",
          path: ["submissionTiming"],
        });
      }
      if (report.submissionTiming && actualTiming !== "normal" && !report.delayReason) {
        context.addIssue({
          code: "custom",
          message: "次日补报或逾期补报必须填写延迟原因",
          path: ["delayReason"],
        });
      }
    }
    if (report.unknownPoints.length !== 3) {
      context.addIssue({ code: "custom", message: "提交晚报时必须填写 3 个不会点", path: ["unknownPoints"] });
    }
    if (report.blockOutcomes.length === 0) {
      context.addIssue({ code: "custom", message: "提交晚报时必须记录学习块结果", path: ["blockOutcomes"] });
    }
    if (report.status === "accepted" && !report.acceptedAt) {
      context.addIssue({ code: "custom", message: "验收通过必须记录时间", path: ["acceptedAt"] });
    }
  });

export const reviewEventSchema = z
  .object({
    id: identifierSchema,
    dayId: identifierSchema,
    kind: z.enum(reviewEventKinds),
    actor: z.enum(reviewActors),
    createdAt: timestampSchema,
    questionId: identifierSchema.optional(),
    prompt: nonEmptyStringSchema.optional(),
    answer: nonEmptyStringSchema.optional(),
    knowledgeMapNodeIds: z.array(identifierSchema).optional(),
    weaknessClusterId: identifierSchema.optional(),
    masteryGrade: z.enum(["A", "B", "C", "D"]).optional(),
    evidence: nonEmptyStringSchema.optional(),
    requestedItems: z.array(nonEmptyStringSchema).optional(),
    relatedPlanRevisionId: identifierSchema.optional(),
  })
  .superRefine((event, context) => {
    if (event.kind === "oral_question_asked" && (!event.questionId || !event.prompt)) {
      context.addIssue({ code: "custom", message: "口试提问必须含题号和题目", path: ["prompt"] });
    }
    if (event.kind === "oral_answer_submitted" && (!event.questionId || !event.answer)) {
      context.addIssue({ code: "custom", message: "口试作答必须含题号和答案", path: ["answer"] });
    }
    if (event.kind === "mastery_assessed" && (!event.masteryGrade || !event.evidence)) {
      context.addIssue({
        code: "custom",
        message: "掌握度判定必须含等级和证据",
        path: ["masteryGrade"],
      });
    }
  });

export const planChangeSchema = z.object({
  id: identifierSchema,
  macroBlockId: identifierSchema,
  kind: z.enum(planChangeKinds),
  before: nonEmptyStringSchema,
  after: nonEmptyStringSchema,
  reason: nonEmptyStringSchema,
  acceptanceCriteria: z.array(nonEmptyStringSchema).min(1),
});

export const planRevisionSchema = z
  .object({
    id: identifierSchema,
    planDate: isoDateSchema,
    fromVersion: z.number().int().positive(),
    toVersion: z.number().int().positive(),
    status: z.enum(planRevisionStatuses),
    createdAt: timestampSchema,
    sourceDayId: identifierSchema,
    evidence: z.array(nonEmptyStringSchema).min(1),
    changes: z.array(planChangeSchema).max(2),
    preservedRequirements: z.array(nonEmptyStringSchema).min(2),
    overloadProtectionApplied: z.boolean(),
    acknowledgedAt: timestampSchema.optional(),
  })
  .superRefine((revision, context) => {
    if (revision.toVersion <= revision.fromVersion) {
      context.addIssue({ code: "custom", message: "新计划版本必须高于原版本", path: ["toVersion"] });
    }
    if (revision.status === "acknowledged" && !revision.acknowledgedAt) {
      context.addIssue({ code: "custom", message: "已确认计划必须记录确认时间", path: ["acknowledgedAt"] });
    }
  });

export const studySeedSchema = z.object({
  version: z.number().int().positive(),
  studyDays: z.array(studyDaySchema),
  practiceAttempts: z.array(practiceAttemptSchema),
  dailyReports: z.array(dailyReportSchema),
  mistakes: z.array(mistakeSchema),
  caseSubmissions: z.array(caseSubmissionSchema),
  reviewEvents: z.array(reviewEventSchema),
  planRevisions: z.array(planRevisionSchema),
});

export type StudyDayInput = z.input<typeof studyDaySchema>;
export type StudySeedInput = z.input<typeof studySeedSchema>;
